import React, { useState, useEffect } from 'react';
import {
  Button,
  Drawer,
  Form,
  Input,
  Select,
  DatePicker,
  Space,
  Popover,
  Tooltip,
  Badge,
  Grid,
} from 'antd';
import { FilterOutlined, CloseOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { SmartTableColumn } from '../types';

const { useBreakpoint } = Grid;

const getFilterFieldKey = (column: SmartTableColumn): string =>
  String(column.filterField ?? column.dataIndex);

const findColumnByFilterKey = (
  cols: SmartTableColumn[],
  key: string
): SmartTableColumn | undefined =>
  cols.find(col => col.filterField === key || col.dataIndex === key);

interface FiltersComponentProps {
  columns: SmartTableColumn[];
  onApplyFilters: (filters: any[]) => void;
  onClearFilters: () => void;
  activeFiltersCount: number;
  autoOpen?: boolean;
  onDrawerClose?: () => void;
  initialFilters?: Record<string, any>;
}

export const FiltersComponent: React.FC<FiltersComponentProps> = ({
  columns,
  onApplyFilters,
  onClearFilters,
  activeFiltersCount,
  autoOpen = false,
  onDrawerClose,
  initialFilters = {},
}) => {
  const [filterForm] = Form.useForm();
  const [visible, setVisible] = useState(false);
  const [activeFilterDetails, setActiveFilterDetails] = useState<any[]>([]);
  const [forceUpdate, setForceUpdate] = useState(0);
  const [dateRangeValues, setDateRangeValues] = useState<{
    [key: string]: any;
  }>({});
  const screens = useBreakpoint();
  const isSmallScreen = !screens.md; // md breakpoint is 768px

  // Initialize form with stored filters
  useEffect(() => {
    if (initialFilters && Object.keys(initialFilters).length > 0) {
      // Convert stored filters to form values
      const formValues: Record<string, any> = {};
      const dateRanges: Record<string, any> = {};
      const filterDetails: any[] = [];
      
      Object.entries(initialFilters).forEach(([key, value]) => {
        const column = findColumnByFilterKey(columns, key);
        
        if (column?.filterType === 'daterange') {
          // For date ranges, the value might be stored as an array or object
          if (Array.isArray(value) && value.length === 2) {
            const dates = [dayjs(value[0]), dayjs(value[1])];
            formValues[key] = dates;
            dateRanges[key] = dates;
            
            // Add to filter details for popup
            filterDetails.push({
              field: key,
              title: column?.title || key,
              value: `${dates[0].format('MMM DD')} - ${dates[1].format('MMM DD')}`,
              type: 'daterange',
            });
          }
        } else if (Array.isArray(value)) {
          // For multiselect
          formValues[key] = value;
          const options = column?.filterOptions || [];
          const selectedLabels = value.map((v: any) => {
            const option = options.find((opt: any) => opt.value === v);
            return option?.label || v;
          });
          filterDetails.push({
            field: key,
            title: column?.title || key,
            value: selectedLabels.join(', '),
            type: 'multiselect',
          });
        } else {
          // For regular filters
          formValues[key] = value;
          const options = column?.filterOptions || [];
          const option = options.find((opt: any) => opt.value === value);
          filterDetails.push({
            field: key,
            title: column?.title || key,
            value: option?.label || value,
            type: 'single',
          });
        }
      });
      
      filterForm.setFieldsValue(formValues);
      setDateRangeValues(dateRanges);
      setActiveFilterDetails(filterDetails);
    }
  }, [initialFilters, columns, filterForm]);

  // Auto-open the filter drawer when autoOpen is true
  useEffect(() => {
    if (autoOpen) {
      setVisible(true);
    }
  }, [autoOpen]);

  // Handle filter form submission
  const handleFilterSubmit = (values: any) => {
    const filters: any[] = [];
    const filterDetails: any[] = [];

    Object.entries(values).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          // Check if it's a date range first
          if (
            value.length === 2 &&
            value[0] &&
            value[1] &&
            dayjs.isDayjs(value[0]) &&
            dayjs.isDayjs(value[1])
          ) {
            // Handle date range
            const startDate = value[0].startOf('day').utc();
            const endDate = value[1].endOf('day').utc();

            filters.push({
              field: key,
              operator: 'gte',
              value: startDate.format(),
            });
            filters.push({
              field: key,
              operator: 'lte',
              value: endDate.format(),
            });

            // Add to filter details for popup
            const column = findColumnByFilterKey(columns, key);
            filterDetails.push({
              field: key,
              title: column?.title || key,
              value: `${value[0].format('MMM DD')} - ${value[1].format(
                'MMM DD'
              )}`,
              type: 'daterange',
            });
          } else if (value.length > 0) {
            // Handle multiple select values
            const filterObject = {
              field: key,
              operator: 'in',
              value,
            };
            filters.push(filterObject);

            // Add to filter details for popup
            const column = findColumnByFilterKey(columns, key);
            const options = column?.filterOptions || [];
            const selectedLabels = value.map((v: any) => {
              const option = options.find((opt: any) => opt.value === v);
              return option?.label || v;
            });
            filterDetails.push({
              field: key,
              title: column?.title || key,
              value: selectedLabels.join(', '),
              type: 'multiselect',
            });
          }
        } else if (typeof value === 'object' && value !== null) {
          // Handle dayjs date objects
          if (dayjs.isDayjs(value)) {
            filters.push({
              field: key,
              operator: 'gte',
              value: value.startOf('day').utc().format('YYYY-MM-DD'),
            });
            filters.push({
              field: key,
              operator: 'lte',
              value: value.endOf('day').utc().format('YYYY-MM-DD'),
            });
          }
        } else {
          // Handle regular values
          filters.push({
            field: key,
            operator: 'eq',
            value,
          });

          // Add to filter details for popup
          const column = findColumnByFilterKey(columns, key);
          const options = column?.filterOptions || [];
          const option = options.find((opt: any) => opt.value === value);
          filterDetails.push({
            field: key,
            title: column?.title || key,
            value: option?.label || value,
            type: 'single',
          });
        }
      }
    });

    // Update active filter details
    setActiveFilterDetails(filterDetails);

    onApplyFilters(filters);
    setVisible(false);
    onDrawerClose?.();
  };

  // Clear all filters
  const handleClearFilters = () => {
    filterForm.resetFields();
    setActiveFilterDetails([]);
    setDateRangeValues({});
    onClearFilters();
    // Close the drawer if it's open
    setVisible(false);
    onDrawerClose?.();
  };

  // Get filterable columns based on filterable property
  const getFilterableColumns = () => {
    return columns.filter(
      col =>
        col.filterable === true &&
        !!(col.dataIndex || col.filterField) &&
        col.key !== 'actions' &&
        !col.key?.includes('action')
    );
  };

  // Date shortcuts for date range pickers
  const getDateShortcuts = () => [
    {
      text: 'Today',
      value: () => [dayjs().startOf('day'), dayjs().endOf('day')],
    },
    {
      text: 'Tomorrow',
      value: () => [
        dayjs().add(1, 'day').startOf('day'),
        dayjs().add(1, 'day').endOf('day'),
      ],
    },
    {
      text: 'Yesterday',
      value: () => [
        dayjs().subtract(1, 'day').startOf('day'),
        dayjs().subtract(1, 'day').endOf('day'),
      ],
    },
    {
      text: 'This Week',
      value: () => [dayjs().startOf('week'), dayjs().endOf('week')],
    },
    {
      text: 'Last Week',
      value: () => [
        dayjs().subtract(1, 'week').startOf('week'),
        dayjs().subtract(1, 'week').endOf('week'),
      ],
    },
    {
      text: 'This Month',
      value: () => [dayjs().startOf('month'), dayjs().endOf('month')],
    },
    {
      text: 'Last Month',
      value: () => [
        dayjs().subtract(1, 'month').startOf('month'),
        dayjs().subtract(1, 'month').endOf('month'),
      ],
    },
    {
      text: 'Last 7 Days',
      value: () => [
        dayjs().subtract(6, 'days').startOf('day'),
        dayjs().endOf('day'),
      ],
    },
    {
      text: 'Last 30 Days',
      value: () => [
        dayjs().subtract(29, 'days').startOf('day'),
        dayjs().endOf('day'),
      ],
    },
  ];

  // Handle date shortcut click
  const handleDateShortcut = (fieldName: string, shortcut: any) => {
    const dates = shortcut.value();

    // Set form values immediately
    filterForm.setFieldsValue({ [fieldName]: dates });

    // Update the date range values state
    setDateRangeValues(prev => ({
      ...prev,
      [fieldName]: dates,
    }));

    // Force form to update and re-render
    setTimeout(() => {
      // Trigger form validation to ensure the field is updated
      filterForm
        .validateFields([fieldName])
        .then(() => {
          // Force re-render
          setForceUpdate(prev => prev + 1);
        })
        .catch(error => {
          console.error('Form validation error:', error);
        });
    }, 100);

    // Don't auto-submit, let user click Apply Filters
  };

  // Active filters popup content
  const activeFiltersContent = (
    <div style={{ minWidth: '200px' }}>
      <div style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '8px' }}>
      </div>
      {activeFilterDetails.map((filter, index) => (
        <div key={index} style={{ marginBottom: '6px' }}>
          <div style={{ fontSize: '11px', fontWeight: 500, color: '#262626' }}>
            {filter.title}:
          </div>
          <div
            style={{ fontSize: '11px', color: '#595959', marginLeft: '8px' }}
          >
            {filter.value}
          </div>
        </div>
      ))}
    </div>
  );

  // Filter button with popup
  const filterButton = (
    <Button
      icon={<FilterOutlined />}
      onClick={() => setVisible(true)}
      type={activeFiltersCount > 0 ? 'primary' : 'default'}
      title="Open filters"
    >
      Filters {activeFiltersCount > 0 && `(${activeFiltersCount})`}
    </Button>
  );

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {activeFiltersCount > 0 ? (
          <Popover
            content={activeFiltersContent}
            title="Applied Filters"
            trigger="hover"
            placement="bottom"
            overlayStyle={{ maxWidth: '300px' }}
          >
            {filterButton}
          </Popover>
        ) : (
          filterButton
        )}
        {activeFiltersCount > 0 && (
          <Tooltip title="Clear all filters">
            <Button
              icon={<CloseOutlined />}
              onClick={handleClearFilters}
              type="primary"
              danger
              size="small"
              style={{ marginLeft: '4px', minWidth: '32px', height: '32px' }}
            />
          </Tooltip>
        )}
      </div>

      <Drawer
        title="Advanced Filters"
        open={visible}
        onClose={() => {
          setVisible(false);
          onDrawerClose?.();
        }}
        width={450}
        footer={
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button onClick={handleClearFilters} style={{ flex: 1 }}>
              Clear All Filters
            </Button>
            <Button
              type="primary"
              onClick={() => filterForm.submit()}
              style={{ flex: 1 }}
            >
              Apply Filters
            </Button>
          </div>
        }
      >
        <Form form={filterForm} layout="vertical" onFinish={handleFilterSubmit}>
          {getFilterableColumns().map(column => {
            const fieldName = getFilterFieldKey(column);
            const fieldTitle = column.title as string;
            const filterType = column.filterType;
            const filterOptions = column.filterOptions;

            // Render different filter types based on column configuration
            switch (filterType) {
              case 'text':
                if (column.filterDependsOn) {
                  return (
                    <Form.Item
                      noStyle
                      shouldUpdate
                      key={`${fieldName}_wrapper`}
                    >
                      {() => {
                        const dependsOnValue = filterForm.getFieldValue(
                          column.filterDependsOn
                        );
                        const isDisabled =
                          !dependsOnValue ||
                          (Array.isArray(dependsOnValue) &&
                            dependsOnValue.length === 0);

                        return (
                          <Form.Item
                            key={fieldName}
                            name={fieldName}
                            label={fieldTitle}
                          >
                            <Input
                              placeholder={`Enter ${fieldTitle}`}
                              onPressEnter={() => filterForm.submit()}
                              disabled={isDisabled}
                            />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                  );
                } else {
                  return (
                    <Form.Item
                      key={fieldName}
                      name={fieldName}
                      label={fieldTitle}
                    >
                      <Input
                        placeholder={`Enter ${fieldTitle}`}
                        onPressEnter={() => filterForm.submit()}
                      />
                    </Form.Item>
                  );
                }

              case 'select':
                if (column.filterDependsOn) {
                  return (
                    <Form.Item
                      noStyle
                      shouldUpdate
                      key={`${fieldName}_wrapper`}
                    >
                      {() => {
                        const dependsOnValue = filterForm.getFieldValue(
                          column.filterDependsOn
                        );
                        const isDisabled =
                          !dependsOnValue ||
                          (Array.isArray(dependsOnValue) &&
                            dependsOnValue.length === 0);

                        return (
                          <Form.Item
                            key={fieldName}
                            name={fieldName}
                            label={fieldTitle}
                          >
                            <Select
                              placeholder={`Select ${fieldTitle}`}
                              allowClear
                              options={filterOptions || []}
                              disabled={isDisabled}
                            />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                  );
                } else {
                  return (
                    <Form.Item
                      key={fieldName}
                      name={fieldName}
                      label={fieldTitle}
                    >
                      <Select
                        placeholder={`Select ${fieldTitle}`}
                        allowClear
                        options={filterOptions || []}
                      />
                    </Form.Item>
                  );
                }

              case 'multiselect':
                if (column.filterDependsOn) {
                  return (
                    <Form.Item
                      noStyle
                      shouldUpdate
                      key={`${fieldName}_wrapper`}
                    >
                      {() => {
                        const dependsOnValue = filterForm.getFieldValue(
                          column.filterDependsOn
                        );
                        const isDisabled =
                          !dependsOnValue ||
                          (Array.isArray(dependsOnValue) &&
                            dependsOnValue.length === 0);

                        return (
                          <Form.Item
                            key={fieldName}
                            name={fieldName}
                            label={fieldTitle}
                          >
                            <Select
                              placeholder={`Select ${fieldTitle}`}
                              allowClear
                              mode="multiple"
                              options={filterOptions || []}
                              maxTagCount="responsive"
                              showSearch
                              optionFilterProp="label"
                              style={{ width: '100%' }}
                              disabled={isDisabled}
                            />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                  );
                } else {
                  return (
                    <Form.Item
                      key={fieldName}
                      name={fieldName}
                      label={fieldTitle}
                    >
                      <Select
                        placeholder={`Select ${fieldTitle}`}
                        allowClear
                        mode="multiple"
                        options={filterOptions || []}
                        maxTagCount="responsive"
                        showSearch
                        optionFilterProp="label"
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  );
                }

              case 'date':
                if (column.filterDependsOn) {
                  return (
                    <Form.Item
                      noStyle
                      shouldUpdate
                      key={`${fieldName}_wrapper`}
                    >
                      {() => {
                        const dependsOnValue = filterForm.getFieldValue(
                          column.filterDependsOn
                        );
                        const isDisabled =
                          !dependsOnValue ||
                          (Array.isArray(dependsOnValue) &&
                            dependsOnValue.length === 0);

                        return (
                          <Form.Item
                            key={fieldName}
                            name={fieldName}
                            label={fieldTitle}
                          >
                            <DatePicker
                              style={{ width: '100%' }}
                              format="YYYY-MM-DD"
                              placeholder={`Select ${fieldTitle}`}
                              disabled={isDisabled}
                            />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                  );
                } else {
                  return (
                    <Form.Item
                      key={fieldName}
                      name={fieldName}
                      label={fieldTitle}
                    >
                      <DatePicker
                        style={{ width: '100%' }}
                        format="YYYY-MM-DD"
                        placeholder={`Select ${fieldTitle}`}
                      />
                    </Form.Item>
                  );
                }

              case 'daterange':
                if (column.filterDependsOn) {
                  return (
                    <Form.Item
                      noStyle
                      shouldUpdate
                      key={`${fieldName}_wrapper`}
                    >
                      {() => {
                        const dependsOnValue = filterForm.getFieldValue(
                          column.filterDependsOn
                        );
                        const isDisabled =
                          !dependsOnValue ||
                          (Array.isArray(dependsOnValue) &&
                            dependsOnValue.length === 0);

                        return (
                          <Form.Item
                            key={fieldName}
                            name={fieldName}
                            label={fieldTitle}
                          >
                            <div>
                              {isSmallScreen ? (
                                // Small screen: Two separate date pickers in one row
                                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                                  <DatePicker
                                    style={{ flex: 1 }}
                                    format="YYYY-MM-DD"
                                    placeholder="Start"
                                    value={dateRangeValues[fieldName]?.[0] || null}
                                    disabled={isDisabled}
                                    onChange={startDate => {
                                      const currentRange = dateRangeValues[fieldName] || [null, null];
                                      const newRange = [startDate, currentRange[1]];
                                      if (startDate || currentRange[1]) {
                                        filterForm.setFieldsValue({ [fieldName]: newRange });
                                        setDateRangeValues(prev => ({
                                          ...prev,
                                          [fieldName]: newRange,
                                        }));
                                      } else {
                                        filterForm.setFieldsValue({ [fieldName]: undefined });
                                        setDateRangeValues(prev => {
                                          const newValues = { ...prev };
                                          delete newValues[fieldName];
                                          return newValues;
                                        });
                                      }
                                    }}
                                  />
                                  <DatePicker
                                    style={{ flex: 1 }}
                                    format="YYYY-MM-DD"
                                    placeholder="End"
                                    value={dateRangeValues[fieldName]?.[1] || null}
                                    disabled={isDisabled}
                                    onChange={endDate => {
                                      const currentRange = dateRangeValues[fieldName] || [null, null];
                                      const newRange = [currentRange[0], endDate];
                                      if (currentRange[0] || endDate) {
                                        filterForm.setFieldsValue({ [fieldName]: newRange });
                                        setDateRangeValues(prev => ({
                                          ...prev,
                                          [fieldName]: newRange,
                                        }));
                                      } else {
                                        filterForm.setFieldsValue({ [fieldName]: undefined });
                                        setDateRangeValues(prev => {
                                          const newValues = { ...prev };
                                          delete newValues[fieldName];
                                          return newValues;
                                        });
                                      }
                                    }}
                                  />
                                </div>
                              ) : (
                                // Large screen: Range picker
                                <DatePicker.RangePicker
                                  key={`${fieldName}-${forceUpdate}`}
                                  style={{ width: '100%', marginBottom: 8 }}
                                  format="YYYY-MM-DD"
                                  placeholder={['Start Date', 'End Date']}
                                  value={dateRangeValues[fieldName] || undefined}
                                  disabled={isDisabled}
                                  onChange={dates => {
                                    if (dates) {
                                      filterForm.setFieldsValue({
                                        [fieldName]: dates,
                                      });
                                      setDateRangeValues(prev => ({
                                        ...prev,
                                        [fieldName]: dates,
                                      }));
                                    } else {
                                      filterForm.setFieldsValue({ [fieldName]: undefined });
                                      setDateRangeValues(prev => {
                                        const newValues = { ...prev };
                                        delete newValues[fieldName];
                                        return newValues;
                                      });
                                    }
                                  }}
                                />
                              )}
                              <div style={{ marginBottom: 8 }}>
                                <div
                                  style={{
                                    fontSize: '12px',
                                    color: '#8c8c8c',
                                    marginBottom: 4,
                                  }}
                                >
                                  Quick shortcuts:
                                </div>
                                <Space wrap size="small">
                                  {getDateShortcuts().map((shortcut, index) => (
                                    <Badge
                                      key={index}
                                      count={shortcut.text}
                                      style={{
                                        cursor: isDisabled
                                          ? 'not-allowed'
                                          : 'pointer',
                                        backgroundColor: isDisabled
                                          ? '#f5f5f5'
                                          : '#e6f7ff',
                                        color: isDisabled
                                          ? '#d9d9d9'
                                          : '#1890ff',
                                        border: isDisabled
                                          ? '1px solid #d9d9d9'
                                          : '1px solid #91d5ff',
                                        borderRadius: '4px',
                                        padding: '2px 8px',
                                        fontSize: '11px',
                                        fontWeight: 'normal',
                                        transition: 'all 0.2s ease',
                                        textAlign: 'center',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minWidth: '60px',
                                        pointerEvents: isDisabled
                                          ? 'none'
                                          : 'auto',
                                      }}
                                      onClick={() =>
                                        !isDisabled &&
                                        handleDateShortcut(fieldName, shortcut)
                                      }
                                      onMouseEnter={e => {
                                        if (!isDisabled) {
                                          e.currentTarget.style.backgroundColor =
                                            '#bae7ff';
                                          e.currentTarget.style.borderColor =
                                            '#69c0ff';
                                          e.currentTarget.style.color =
                                            '#096dd9';
                                        }
                                      }}
                                      onMouseLeave={e => {
                                        if (!isDisabled) {
                                          e.currentTarget.style.backgroundColor =
                                            '#e6f7ff';
                                          e.currentTarget.style.borderColor =
                                            '#91d5ff';
                                          e.currentTarget.style.color =
                                            '#1890ff';
                                        }
                                      }}
                                    />
                                  ))}
                                </Space>
                              </div>
                            </div>
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                  );
                } else {
                  return (
                    <Form.Item
                      key={fieldName}
                      name={fieldName}
                      label={fieldTitle}
                    >
                      <div>
                        {isSmallScreen ? (
                          // Small screen: Two separate date pickers in one row
                          <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                            <DatePicker
                              style={{ flex: 1 }}
                              format="YYYY-MM-DD"
                              placeholder="Start"
                              value={dateRangeValues[fieldName]?.[0] || null}
                              onChange={startDate => {
                                const currentRange = dateRangeValues[fieldName] || [null, null];
                                const newRange = [startDate, currentRange[1]];
                                if (startDate || currentRange[1]) {
                                  filterForm.setFieldsValue({ [fieldName]: newRange });
                                  setDateRangeValues(prev => ({
                                    ...prev,
                                    [fieldName]: newRange,
                                  }));
                                } else {
                                  filterForm.setFieldsValue({ [fieldName]: undefined });
                                  setDateRangeValues(prev => {
                                    const newValues = { ...prev };
                                    delete newValues[fieldName];
                                    return newValues;
                                  });
                                }
                              }}
                            />
                            <DatePicker
                              style={{ flex: 1 }}
                              format="YYYY-MM-DD"
                              placeholder="End"
                              value={dateRangeValues[fieldName]?.[1] || null}
                              onChange={endDate => {
                                const currentRange = dateRangeValues[fieldName] || [null, null];
                                const newRange = [currentRange[0], endDate];
                                if (currentRange[0] || endDate) {
                                  filterForm.setFieldsValue({ [fieldName]: newRange });
                                  setDateRangeValues(prev => ({
                                    ...prev,
                                    [fieldName]: newRange,
                                  }));
                                } else {
                                  filterForm.setFieldsValue({ [fieldName]: undefined });
                                  setDateRangeValues(prev => {
                                    const newValues = { ...prev };
                                    delete newValues[fieldName];
                                    return newValues;
                                  });
                                }
                              }}
                            />
                          </div>
                        ) : (
                          // Large screen: Range picker
                          <DatePicker.RangePicker
                            key={`${fieldName}-${forceUpdate}`}
                            style={{ width: '100%', marginBottom: 8 }}
                            format="YYYY-MM-DD"
                            placeholder={['Start Date', 'End Date']}
                            value={dateRangeValues[fieldName] || undefined}
                            onChange={dates => {
                              if (dates) {
                                filterForm.setFieldsValue({ [fieldName]: dates });
                                setDateRangeValues(prev => ({
                                  ...prev,
                                  [fieldName]: dates,
                                }));
                              } else {
                                filterForm.setFieldsValue({ [fieldName]: undefined });
                                setDateRangeValues(prev => {
                                  const newValues = { ...prev };
                                  delete newValues[fieldName];
                                  return newValues;
                                });
                              }
                            }}
                          />
                        )}
                        <div style={{ marginBottom: 8 }}>
                          <div
                            style={{
                              fontSize: '12px',
                              color: '#8c8c8c',
                              marginBottom: 4,
                            }}
                          >
                            Quick shortcuts:
                          </div>
                          <Space wrap size="small">
                            {getDateShortcuts().map((shortcut, index) => (
                              <Badge
                                key={index}
                                count={shortcut.text}
                                style={{
                                  cursor: 'pointer',
                                  backgroundColor: '#e6f7ff',
                                  color: '#1890ff',
                                  border: '1px solid #91d5ff',
                                  borderRadius: '4px',
                                  padding: '2px 8px',
                                  fontSize: '11px',
                                  fontWeight: 'normal',
                                  transition: 'all 0.2s ease',
                                  textAlign: 'center',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  minWidth: '60px',
                                }}
                                onClick={() =>
                                  handleDateShortcut(fieldName, shortcut)
                                }
                                onMouseEnter={e => {
                                  e.currentTarget.style.backgroundColor =
                                    '#bae7ff';
                                  e.currentTarget.style.borderColor = '#69c0ff';
                                  e.currentTarget.style.color = '#096dd9';
                                }}
                                onMouseLeave={e => {
                                  e.currentTarget.style.backgroundColor =
                                    '#e6f7ff';
                                  e.currentTarget.style.borderColor = '#91d5ff';
                                  e.currentTarget.style.color = '#1890ff';
                                }}
                              />
                            ))}
                          </Space>
                        </div>
                      </div>
                    </Form.Item>
                  );
                }

              default:
                return (
                  <Form.Item
                    key={fieldName}
                    name={fieldName}
                    label={fieldTitle}
                  >
                    <Input
                      placeholder={`Enter ${fieldTitle}`}
                      onPressEnter={() => filterForm.submit()}
                    />
                  </Form.Item>
                );
            }
          })}
        </Form>
      </Drawer>
    </>
  );
};
